import { ModuleContext } from "./ModuleContext"
import { useReducer, useState, useEffect } from "react"
import React from "react"
import { checkComponentResponseCompletion } from "./utils"
import { initialModuleState } from "../initialModuleState"
import cloneDeep from "lodash/cloneDeep"
import { v4 as uuidv4 } from "uuid"

/**
 * Reducer function to manage the state of a module, these actions can be triggered during the creation / editing of a module as well as when a user is taking the module.
 * This represents the central logic of every module wether it be a student answering question on the module or an educator composing the module itself.
 * @param {Object} state - Current state of the module.
 * @param {Object} action - Action to perform on the state.
 * @param {string} action.type - Type of the action, detetmines the dispatch function.
 * @param {number} [action.slideIndex] - Index of the slide (if applicable).
 * @param {number} [action.componentIndex] - Index of the component (if applicable).
 * @param {number} [action.sectionIndex] - Index of the section (if applicable).
 * @param {Object} [action.payload] - Payload for the action.
 * @returns {Object} Updated state of the module.
 */
const moduleReducer = (state, action) => {
  // will use this to maintain the state of the module itself, will change with userResponse
  const { slideIndex, componentIndex, sectionIndex, payload } = action

  let newState = { ...state }

  switch (action.type) {
    /**
     * Adds a new slide to the specified section.
     * @param {number} sectionIndex - Index of the section to add the slide to.
     */
    case "ADD_SLIDE":
      // adds a new slide to the module, may add payload depending on how UI works. If slide is created with name or is blank
      newState.sections[sectionIndex]?.sectionSlides.push({
        uuid: uuidv4(),
        slideTitle: "",
        slideTitleEs: "",
        slideContent: [],
      })
      return newState

    /**
     * Adds a new section to the module.
     */
    case "ADD_SECTION":
      // adds a new section to the module
      newState.sections.push({
        uuid: uuidv4(),
        sectionTitle: "",
        sectionTitleEs: "",
        sectionTime: "",
        sectionTimeEs: "",
        sectionDescription: "",
        sectionDescriptionEs: "",
        sectionDirections: "",
        sectionDirectionsEs: "",
        sectionSlides: [
          {
            slideTitle: "",
            slideTitleEs: "",
            slideContent: [],
            uuid: uuidv4(),
          },
        ],
      })
      return newState

    case "UPDATE_MODULE_INTERNAL_NOTES":
      // updates the internal notes of the module
      newState.internalNotes = payload
      return newState

    /**
     * Updates the title of the module.
     * @param {string} payload - New title for the module.
     */
    case "UPDATE_MODULE_TITLE":
      newState.title = payload
      return newState

    /**
     * Updates a key-value pair in the module state.
     * @param {string} action.key - Key to update.
     * @param {any} payload - New value for the key.
     */
    case "UPDATE_MODULE_KEY_VALUE":
      newState[action.key] = payload
      return newState

    /**
     * Updates the title of a specific section.
     * @param {number} sectionIndex - Index of the section to update.
     * @param {string} payload - New title for the section.
     */
    case "UPDATE_SECTION_TITLE":
      // updates the title of a specific section
      newState.sections[sectionIndex].sectionTitle = payload
      return newState

    /**
     * Updates the title of a specific slide.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide to update.
     * @param {string} payload - New title for the slide.
     */
    case "UPDATE_SLIDE_TITLE":
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideTitle = payload
      return newState
    
    /**
     * Reorder the slides within a section
     * @param {number} sectionIndex - Index of the section containing the slides.
     * @param {Object} payload - Array of slide UUIDs representing new order.
     */
    case "UPDATE_SLIDE_ORDER":
      const idToSlide = {}
      newState.sections[sectionIndex].sectionSlides.forEach(slide => idToSlide[slide.uuid] = slide)
      newState.sections[sectionIndex].sectionSlides = payload.map(id => idToSlide[id])
      return newState
    
    /**
     * Deletes a slide from a section. Ensures at least one slide remains in the section.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide to delete.
     */
    case "DELETE_SLIDE":
      newState.sections[sectionIndex].sectionSlides.splice(slideIndex, 1)
      if (newState.sections[sectionIndex].sectionSlides.length === 0) {
        newState.sections[sectionIndex].sectionSlides.push({
          slideTitle: "",
          slideTitleEs: "",
          slideContent: [],
        })
      }
      return newState

    /**
     * Updates a specific component in a slide.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide containing the component.
     * @param {number} componentIndex - Index of the component to update.
     * @param {Object} payload - New data for the component.
     */
    case "UPDATE_COMPONENT":
      // replaces existing component data with new payload, can be used for updating questions from editor
      // auth only
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent.splice(componentIndex, 1, payload)
      return newState

    /**
     * Deletes a specific component from a slide.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide containing the component.
     * @param {number} componentIndex - Index of the component to delete.
     */
    case "DELETE_COMPONENT":
      newState?.sections[sectionIndex]?.sectionSlides[slideIndex]?.slideContent?.splice(componentIndex, 1)
      return newState

    /**
     * Adds a new component to the end of a slide.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide to add the component to.
     * @param {Object} payload - Data for the new component.
     */
    case "PUSH_COMPONENT_TO_SLIDE_END":
      // inserts a new component into the slideContent array, will be used for adding new questions from editor
      // auth only
      // can use specified slide or perhaps default to last avaliable slide

      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent.push(payload)
      return newState

    /**
     * Inserts a new component after a specific position in a slide.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide to add the component to.
     * @param {number} componentIndex - Index after which to insert the component.
     * @param {Object} payload - Data for the new component.
     */
    case "INSERT_COMPONENT_AFTER_POSITION":
      // inserts a new component into the slideContent array, will be used for adding new questions from editor at specific positions
      // auth only
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent.splice(componentIndex + 1, 0, payload)
      return newState
    
    /**
     * Generate a UUID for a component
     * @param {number} sectionIndex - Index of the section containing the component.
     * @param {number} slideIndex - Index of the slide containing the component.
     * @param {number} componentIndex - Index of the component to update.
     */
    case "GENERATE_COMPONENT_UUID":
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent[componentIndex].uuid = uuidv4()
      return newState
    
    /**
     * Reorder components in a slide
     * @param {number} sectionIndex - Index of the section containing the reordered components.
     * @param {number} slideIndex - Index of the slide containing the reordered components.
     * @param {Object} payload - Array of component UUIDs representing the new component order
     */
    case "UPDATE_COMPONENT_ORDER":
      const idToComp = {}
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent.forEach(comp => idToComp[comp.uuid] = comp)
      newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent = payload.map(id => idToComp[id])
      return newState

    /**
     * Records a user's response for a specific component.
     * @param {number} sectionIndex - Index of the section containing the slide.
     * @param {number} slideIndex - Index of the slide containing the component.
     * @param {number} componentIndex - Index of the component to record the response for.
     * @param {Object} payload - User's response data.
     * @param {boolean} [action.isTableComponent] - Whether the component is part of a table.
     * @param {Object} [action.cellCoordinates] - Coordinates of the cell in the table (if applicable).
     */
    case "RECORD_RESPONSE":
      // updates the user response for a specific component
      // user only
      // need to put deeper into state
      if (action?.isTableComponent) {
        const { cellCoordinates } = action
        const { rowIndex, colIndex } = cellCoordinates

        newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent[componentIndex].rows[rowIndex][colIndex].userResponse = payload
      } else {
        newState.sections[sectionIndex].sectionSlides[slideIndex].slideContent[componentIndex].userResponse = payload
      }
      return newState

    /**
     * Loads the full module state from a provided payload.
     * @param {Object} payload - Full module state to load.
     */
    case "LOAD_FULL_MODULE":
      // updates the module state with the full module data
      newState = payload
      return newState

    // editor actions

    /**
     * Updates the title of the module.
     * @param {string} payload - New title for the module.
     */
    case "UPDATE_MODULE_TITLE":
      newState.title = payload
      return newState

    /**
     * Updates the category of the module.
     * @param {string} payload - New category for the module.
     */
    case "UPDATE_MODULE_CATEGORY":
      newState.category = payload
      return newState

    /**
     * Updates the type of the module.
     * @param {string} payload - New type for the module (e.g., "NACE", "K-12").
     */
    case "UPDATE_MODULE_TYPE":
      newState.type = payload
      return newState

    /**
     * Updates the banner image of the module.
     * @param {string} payload - URL of the new banner image.
     */
    case "UPDATE_MODULE_BANNER_IMAGE":
      newState.image = payload
      return newState

    /**
     * Updates the banner color of the module.
     * @param {string} payload - New banner color for the module.
     */
    case "UPDATE_MODULE_BANNER_COLOR":
      newState.bannerColor = payload
      return newState

    /**
     * Updates the short description of the module.
     * @param {string} payload - New short description for the module.
     */
    case "UPDATE_MODULE_SHORT_DESCRIPTION":
      newState.shortDescription = payload
      return newState

    /**
     * Updates the "Why Complete This Module" description.
     * @param {string} payload - New full description for the module.
     */
    case "UPDATE_MODULE_WHY_COMPLETE":
      newState.fullDescription = payload
      return newState

    /**
     * Updates the estimated time to complete the module.
     * @param {string} payload - New estimated time for the module.
     */
    case "UPDATE_MODULE_ESTIMATED_TIME":
      newState.time = payload
      return newState

    /**
     * Updates the quote displayed in the module.
     * @param {string} payload - New quote for the module.
     */
    case "UPDATE_MODULE_QUOTE":
      newState.quote = payload
      return newState

    /**
     * Updates the author of the quote displayed in the module.
     * @param {string} payload - New quote author for the module.
     */
    case "UPDATE_MODULE_QUOTE_AUTHOR":
      newState.quoteAuthor = payload
      return newState

    /**
     * Updates a specific skill in the module's skills list.
     * @param {Object} action.payload - Contains the skill index and the new skill value.
     * @param {number} action.payload.skillIndex - Index of the skill to update.
     * @param {string} action.payload.payload - New value for the skill.
     */
    case "UPDATE_MODULE_SKILL":
      newState.skills[action.payload.skillIndex] = action.payload.payload
      return newState

    /**
     * Removes a skill from the module's skills list.
     * @param {number} action.payload - Index of the skill to remove.
     */
    case "REMOVE_MODULE_SKILL":
      newState.skills.splice(action.payload, 1)
      return newState

    /**
     * Adds a new skill to the module's skills list.
     * The new skill is initialized as an empty string.
     */
    case "ADD_MODULE_SKILL":
      newState.skills.push("")
      return newState

    /**
     * Updates the title of a specific section in the module.
     * @param {number} sectionIndex - Index of the section to update.
     * @param {string} payload - New title for the section.
     */
    case "UPDATE_MODULE_SECTION_TITLE":
      newState.sections[sectionIndex].sectionTitle = payload
      return newState

    /**
     * Updates the estimated time for a specific section in the module.
     * @param {number} sectionIndex - Index of the section to update.
     * @param {string} payload - New estimated time for the section.
     */
    case "UPDATE_MODULE_SECTION_ESTIMATED_TIME":
      newState.sections[sectionIndex].sectionTime = payload
      return newState

    /**
     * Updates the "What You Will Learn" description for the module.
     * @param {string} payload - New description for "What You Will Learn".
     */
    case "UPDATE_MODULE_PROGRAM_LEARN":
      newState.whatLearn = payload
      return newState

    /**
     * Updates the directions for completing the module.
     * @param {string} payload - New directions for the module.
     */
    case "UPDATE_MODULE_PROGRAM_DIRECTIONS":
      newState.directions = payload
      return newState

    /**
     * Updates the "What You Will Learn" description for a specific section.
     * @param {number} sectionIndex - Index of the section to update.
     * @param {string} payload - New description for the section.
     */
    case "UPDATE_MODULE_SECTION_LEARN":
      newState.sections[sectionIndex].sectionDescription = payload
      return newState

    /**
     * Updates the directions for a specific section in the module.
     * @param {number} sectionIndex - Index of the section to update.
     * @param {string} payload - New directions for the section.
     */
    case "UPDATE_MODULE_SECTION_DIRECTIONS":
      newState.sections[sectionIndex].sectionDirections = payload
      return newState
    
    /**
     * Swap the positions of two sections
     * @param {number} sectionIndex - index of a section
     * @param {number} payload - index of the section to swap positions with
     */
    case "SWAP_SECTION_POSITIONS":
      const movedSection = newState.sections[sectionIndex]
      newState.sections[sectionIndex] = newState.sections[payload]
      newState.sections[payload] = movedSection
      return newState
    
    /**
     * Deletes a section from the module. Ensures at least one section remains in the module.
     * @param {number} sectionIndex - Index of the section to delete.
     */
    case "DELETE_MODULE_SECTION":
      newState.sections.splice(sectionIndex, 1)
      if (newState.sections.length === 0) {
        // Add a default section if no sections remain
        newState.sections.push({
          uuid: uuidv4(),
          sectionTitle: "",
          sectionTitleEs: "",
          sectionTime: "",
          sectionTimeEs: "",
          sectionDescription: "",
          sectionDescriptionEs: "",
          sectionDirections: "",
          sectionDirectionsEs: "",
          sectionSlides: [
            {
              slideTitle: "",
              slideTitleEs: "",
              slideContent: [],
            },
          ],
        })
      }
      return newState

    /**
     * Resets the module state to its initial blank state.
     */
    case "RESET_MODULE":
      // resets the module state to the initial state
      const blankState = cloneDeep(initialModuleState)
      blankState.sections[0].uuid = uuidv4()
      blankState.sections[0].sectionSlides[0].uuid = uuidv4()
      return blankState

    default:
      return state
  }
}

/**
 * If any changes have been made to how modules are created/stored since this 
 * module was last updated, update the module to match the new format. To prevent
 * divergence, this currently only updates the module if the module is being 
 * accessed in "EDITOR" mode.
 * @param {Object} moduleJson - Initial json data of the module.
 * @param {string} mode - Mode of the module (e.g., "EDITOR", "STUDENT", "SUBMISSION", "PREVIEW")
 * @returns {Object} moduleJson, but updated to match the new module format
 */
const updateOlderModuleFormats = (moduleJson, mode) => {
  if (mode !== "EDITOR") {
    return moduleJson
  }

  if (moduleJson && moduleJson.sections) {
    moduleJson.sections.forEach(section => {
      // Make sure any sectionTime's will be translated
      section.sectionTime = section.sectionTime || ""
      section.sectionTimeEs = ""

      // Make sure all sections, slides, and components have unique IDs
      section.uuid = section.uuid || uuidv4()
      if (section.sectionSlides) {
        section.sectionSlides.forEach(slide => {
          slide.uuid = slide.uuid || uuidv4()
          if (slide.slideContent) {
            slide.slideContent.forEach(component => {
              component.uuid = component.uuid || uuidv4()
            })
          }
        })
      }
    })
  }

  if (moduleJson && !moduleJson.internalNotes) {
    moduleJson.internalNotes = ""
  }

  return moduleJson
}

/**
 * Custom hook to manage the state of a module, to be called in context provider to provide access to all modules regardless of mode
 * 1. Invokes the reducer to manage the module state based on the initial state provided
 * 2. Provides functions to export and import module data
 * 3. Handles side effects related to module state changes
 * 4. Provides the module UI state and functions to manage the UI interactions
 * 5. Tracks whether the module has been edited for unsaved changes
 * @param {Object} initialState - Initial json data of the module.
 * @param {string} mode - Mode of the module (e.g., "EDITOR", "STUDENT", "SUBMISSION", "PREVIEW")
 * @param {Object} moduleContentRef - Reference to the module content DOM element.
 * @param {string} status - Status of the module (e.g., "in_progress", "completed").
 * @returns {Object} Module state, dispatch function, UI state, and export/import functions.
 */
const useModuleState = (initialState, mode, moduleContentRef, status) => {
  // need to use deep copy here to avoid mutating the initialState
  const initialStateCopy = JSON.parse(JSON.stringify(initialState))
  const updatedInitialState = updateOlderModuleFormats(initialStateCopy, mode)
  const [moduleState, dispatch] = useReducer(moduleReducer, updatedInitialState)
  const moduleUI = useModuleUI(moduleState, mode, moduleContentRef, status)

  const [hasModuleBeenEdited, setHasModuleBeenEdited] = useState(false)

  useEffect(() => {
    // Check if the module state has been edited
    const isModuleUnchanged = JSON.stringify(moduleState) === JSON.stringify(initialState)

    if (isModuleUnchanged) {
      setHasModuleBeenEdited(false)
    } else {
      setHasModuleBeenEdited(true)
    }
    // setHasModuleBeenEdited(hasChanges)
  }, [moduleState, initialState])

  useEffect(() => {
    if (initialState) {
      dispatch({
        type: "LOAD_FULL_MODULE",
        payload: updatedInitialState,
      })
    }
  }, [initialState])

  const exportModuleState = () => {
    const moduleCopy = JSON.parse(JSON.stringify(moduleState))
    const jsonString = JSON.stringify(updateOlderModuleFormats(moduleCopy, mode), null, 2)
    const blob = new Blob([jsonString], { type: "text/plain" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = `${moduleState.title}.json` // File name based on title
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const importModuleState = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const uploadedState = e.target.result
        try {
          const parsedState = updateOlderModuleFormats(JSON.parse(uploadedState), mode)
          // confirm user wants to do this before overwriting
          if (typeof window !== "undefined") {
            const confirmUpload = window.confirm("Are you sure you want to upload this module? This will overwrite your current module.")
            if (confirmUpload) {
              dispatch({
                type: "LOAD_FULL_MODULE",
                payload: parsedState,
              })
            }
          }
        } catch (error) {
          console.error("Error parsing imported module state:", error)
        }
      }
      reader.readAsText(file)
    }
  }

  return {
    moduleState,
    dispatch,
    moduleUI,
    exportModuleState,
    importModuleState,
    hasModuleBeenEdited,
  }
}

/**
 * Custom hook to manage the UI state of a module, including navigation and completion tracking.
 * Allows for decoupled UI logic 
 * @param {Object} moduleState - Current state of the module.
 * @param {string} mode - Mode of the module (e.g., "EDITOR", "STUDENT", "SUBMISSION", "PREVIEW"), used to determine various UI behaviors and restrictions.
  1. STUDENT: User is taking the module, can navigate through slides and sections, and complete the module but are limited to completing the module in a linear fashion.
  2. EDITOR: User is editing the module, can navigate freely and edit any part of the module.
  3. SUBMISSION: User is viewing the module submission, can navigate through slides and sections, but cannot edit the module.
  4. PREVIEW: User is previewing the module, can navigate freely and view the module but cannot edit or complete it.
 * @param {Object} moduleContentRef - Reference to the module content DOM element.
 * @param {string} status - Status of the module (e.g., "in_progress", "completed").
 * @returns {Object} UI state and functions for managing module navigation and UI interactions.
 */
const useModuleUI = (moduleState, mode, moduleContentRef, status) => {
  const [isSlideView, setIsSlideView] = useState(true)

  // coordinates for the current section, slide, and component used for component insertion and editing
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [currentComponentIndex, setCurrentComponentIndex] = useState(0)

  const [isComponentSidebarOpen, setIsComponentSidebarOpen] = useState(false)

  /**
   * Locally tracked completion status used for immediate UI updates in cached layer
   */
  const [completionStatus, setCompletionStatus] = useState(status)

  // used for editor page when editing the overview of a section
  const [currentEditingSectionIndex, setCurrentEditingSectionIndex] = useState(null)
  const [isEditingSectionOverview, setIsEditingSectionOverview] = useState(false)

  const [isEditingOverview, setIsEditingOverview] = useState(mode === "EDITOR" ? true : false)

  const calculatePercentageComplete = () => {
    if (!moduleState?.sections || moduleState.sections.length === 0) {
      return 0 // No sections or slides, progress is 0%
    }

    // Total slides across all sections
    const totalSlides = moduleState.sections.reduce((total, section) => total + (section.sectionSlides?.length || 0), 0)

    if (totalSlides === 0) {
      return 0 // Avoid division by zero
    }

    // Slides completed up to the current section
    const slidesBeforeCurrentSection = moduleState.sections
      .slice(0, currentSectionIndex)
      .reduce((total, section) => total + (section.sectionSlides?.length || 0), 0)

    // Slides completed within the current section up to the current slide
    const slidesInCurrentSection = currentSlideIndex + 1

    // Total completed slides
    const completedSlides = slidesBeforeCurrentSection + slidesInCurrentSection

    // Calculate percentage
    return Math.min((completedSlides / totalSlides) * 100, 100) // Ensure it doesn't exceed 100%
  }

  // Use the function to set percentageComplete
  const moduleCompletionPercentage = calculatePercentageComplete()

  useEffect(() => {
    if (status === "completed") {
      setCompletionStatus("completed")
    } else {
      setCompletionStatus("in_progress")
    }
  }, [status])

  const isCurrentSlideInProgress = moduleState?.sections[currentSectionIndex]?.sectionSlides[currentSlideIndex]?.slideContent?.some(
    (component) => !checkComponentResponseCompletion(component)
  )

  // data used to map out the current sections slides with completion status used to determine if the user can move to the next slide
  const currentSectionSlideData =
    moduleState?.sections[currentSectionIndex]?.sectionSlides?.map((slide) => {
      const { slideTitle, slideContent } = slide
      return {
        slideTitle: slideTitle,
        // make every slide considered complete if in submission view
        isComplete: mode === "STUDENT" ? slideContent.every((component) => checkComponentResponseCompletion(component)) : true,
      }
    }) || []

  // data used to map out if section is complete or not based upon user responses not being null, and criteria being met per component
  const sectionCompletionData = moduleState.sections.map((section, index, arr) => {
    const { sectionTitle, sectionSlides, sectionTitleEs, sectionTime, sectionTimeEs } = section

    return {
      sectionTitle: sectionTitle,
      sectionTitleEs: sectionTitleEs,
      sectionTime: sectionTime,
      sectionTimeEs: sectionTimeEs,
      isComplete:
        mode === "STUDENT"
          ? sectionSlides.every((slide) =>
            slide.slideContent.every((component) => {
              // validation on a component level to determine if the component is complete
              return component.userResponse !== null
            })
          )
          : true,
      isLocked:
        mode === "STUDENT"
          ? arr[index - 1]?.sectionSlides
            ?.map((slide) => slide.slideContent)
            .flat()
            .some((component) => !checkComponentResponseCompletion(component))
          : false,
    }
  })

  const isModuleComplete = mode === "STUDENT" ? sectionCompletionData.every((section) => section.isComplete) : true

  const isCurrentSlideLast =
    currentSectionIndex === moduleState.sections.length - 1 &&
    currentSlideIndex === moduleState.sections[currentSectionIndex].sectionSlides.length - 1

  const isCurrentSlideFirst = currentSectionIndex === 0 && currentSlideIndex === 0

  const getNextSlideCoordinates = () => {
    const { sections } = moduleState

    if (currentSectionIndex < 0 || currentSectionIndex >= sections.length) {
      throw new Error("Invalid section index")
    }

    const currentSection = sections[currentSectionIndex]
    const totalSlidesInCurrentSection = currentSection.sectionSlides.length

    // Move to the next slide within the same section
    if (currentSlideIndex + 1 < totalSlidesInCurrentSection) {
      return {
        sectionIndex: currentSectionIndex,
        slideIndex: currentSlideIndex + 1,
      }
    }

    // Move to the first slide of the next section
    if (currentSectionIndex + 1 < sections.length) {
      return {
        sectionIndex: currentSectionIndex + 1,
        slideIndex: 0,
      }
    }

    // If no more slides or sections, return null (or handle as needed)
    return null
  }

  const scrollToModuleTop = () => {
    if (typeof window !== "undefined" && moduleContentRef?.current) {
      moduleContentRef.current.scrollIntoView({ behavior: "auto", block: "start" })
    }
  }

  function getPreviousSlideCoordinates() {
    const { sections } = moduleState

    if (currentSectionIndex < 0 || currentSectionIndex >= sections.length) {
      throw new Error("Invalid section index")
    }

    // Move to the previous slide within the same section
    if (currentSlideIndex > 0) {
      return {
        sectionIndex: currentSectionIndex,
        slideIndex: currentSlideIndex - 1,
      }
    }

    // Move to the last slide of the previous section
    if (currentSectionIndex > 0) {
      const previousSection = sections[currentSectionIndex - 1]
      const lastSlideIndexInPreviousSection = previousSection.sectionSlides.length - 1

      return {
        sectionIndex: currentSectionIndex - 1,
        slideIndex: lastSlideIndexInPreviousSection,
      }
    }

    // If at the beginning of the first slide, return null (or handle as needed)
    return null
  }

  const toggleSlideView = () => {
    setIsSlideView(!isSlideView)
  }

  const goToNextSlide = () => {
    // will need logic to check if next slide is allowed

    if (isCurrentSlideInProgress && mode === "STUDENT") {
      alert("Please complete the slide before moving to the next one")
      return
    }

    if (isCurrentSlideLast) {
      alert("This is the last slide")
      return
    }

    const coordinates = getNextSlideCoordinates()

    if (coordinates) {
      scrollToModuleTop()
      setCurrentSectionIndex(coordinates.sectionIndex)
      setCurrentSlideIndex(coordinates.slideIndex)
    }
  }

  const goToSectionAtIndex = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex)
    setCurrentSlideIndex(0)
    scrollToModuleTop()
  }

  // used for interactive view
  const goToSlideAtIndex = (slideIndex) => {
    const slidesBefore = currentSectionSlideData.slice(0, slideIndex)
    if (slidesBefore.some((slide) => !slide.isComplete)) {
      // alert("Please complete the previous slides before moving to this one")
      return
    }
    scrollToModuleTop()
    setCurrentSlideIndex(slideIndex)
  }

  const goToPreviousSlide = () => {
    const coordinates = getPreviousSlideCoordinates()

    if (coordinates) {
      scrollToModuleTop()
      setCurrentSectionIndex(coordinates.sectionIndex)
      setCurrentSlideIndex(coordinates.slideIndex)
    }
  }

  const handleModuleCompletion = () => {
    if (isModuleComplete) {
      setCompletionStatus("completed")
    }
  }

  const openSidebarAndSetCooridinates = (coordinates) => {
    const { sectionIndex, slideIndex, componentIndex } = coordinates
    setIsComponentSidebarOpen(true)
    setCurrentSectionIndex(sectionIndex)
    setCurrentSlideIndex(slideIndex)
    setCurrentComponentIndex(componentIndex)
  }

  // function to reset the module UI to its initial state for students taking the module again
  const resetModuleUI = () => {
    setCompletionStatus("in_progress")
    setCurrentComponentIndex(0)
    setCurrentSectionIndex(0)
    setCurrentSlideIndex(0)
    setIsComponentSidebarOpen(false)
  }

  const handleViewResponsesReset = () => {
    setIsSlideView(false)
    setCurrentComponentIndex(0)
    setCurrentSectionIndex(0)
    setCurrentSlideIndex(0)
  }

  // go to specific point in module without validation, used for editor
  const goToSectionAndSlideAsAdmin = (sectionIndex, slideIndex) => {
    setIsEditingOverview(false)
    setIsEditingSectionOverview(false)
    setCurrentEditingSectionIndex(null)
    setCurrentSectionIndex(sectionIndex)
    setCurrentSlideIndex(slideIndex)
    scrollToModuleTop()
  }

  const goToLastSlideInSection = (sectionIndex) => {
    const currentSection = moduleState.sections[sectionIndex]
    const lastSlideIndex = currentSection.sectionSlides.length - 1
    setIsEditingOverview(false)
    setIsEditingSectionOverview(false)
    setCurrentEditingSectionIndex(null)
    setCurrentSectionIndex(sectionIndex)
    setCurrentSlideIndex(lastSlideIndex)
    setCurrentComponentIndex(0)
    if (currentSection) {
      const lastSlideIndex = currentSection.sectionSlides.length - 1
      setCurrentSlideIndex(lastSlideIndex + 1)
      scrollToModuleTop()
    }
  }

  const goToEditorOverview = () => {
    setIsEditingOverview(true)
    setIsEditingSectionOverview(false)
    setCurrentEditingSectionIndex(null)
    scrollToModuleTop()
  }

  const goToLastSection = () => {
    const lastSectionIndex = moduleState.sections.length
    setIsEditingSectionOverview(true)
    setIsEditingOverview(false)
    setCurrentEditingSectionIndex(lastSectionIndex)
    scrollToModuleTop()
  }

  return {
    mode,
    isSlideView,
    currentSectionIndex,
    currentSlideIndex,
    currentComponentIndex,
    isComponentSidebarOpen,
    isCurrentSlideInProgress,
    isCurrentSlideLast,
    isCurrentSlideFirst,
    isModuleComplete,
    currentSectionSlideData,
    sectionCompletionData,
    completionStatus,
    isEditingOverview,
    currentEditingSectionIndex,
    isEditingSectionOverview,
    moduleCompletionPercentage,
    setIsEditingOverview,
    setIsComponentSidebarOpen,
    toggleSlideView,
    setCurrentSectionIndex,
    setCurrentSlideIndex,
    goToNextSlide,
    goToPreviousSlide,
    goToSectionAtIndex,
    goToSlideAtIndex,
    openSidebarAndSetCooridinates,
    handleModuleCompletion,
    handleViewResponsesReset,
    setCompletionStatus,
    resetModuleUI,
    goToSectionAndSlideAsAdmin,
    goToLastSlideInSection,
    setCurrentEditingSectionIndex,
    setIsEditingSectionOverview,
    goToEditorOverview,
    goToLastSection,
  }
}

/**
 * Context provider for managing the state and UI of a module.
 * @param {React.ReactNode} props.children - Child components to render within the provider.
 * @param {Object} props.initialState - Initial state of the module, the module JSON output from the reducer and stored in the SQL database, contains module data and user responses.
 * @param {string} props.mode - Mode of the module ("EDITOR" || "STUDENT" || "PREVIEW" || "SUBMISSION"), determines how the module is displayed how components behave
 * @param {Object} props.moduleContentRef - Reference to the module content DOM element.
 * @param {string} props.moduleId - ID of the module in SQL table.
 * @param {string} props.programId - ID of the program in SQL table.
 * @param {string} props.status - Status of the module (null || "in_progress" || "completed").
 * @param {Function} props.loadUserModuleRecord - Function to load the user's module record.
 * @returns {JSX.Element} The module context provider.
 */
export const ModuleProvider = ({ children, initialState, mode, moduleContentRef, moduleId, programId, status, loadUserModuleRecord, accessType, setAccessType }) => {
  const { moduleState, dispatch, moduleUI, exportModuleState, importModuleState, hasModuleBeenEdited } = useModuleState(
    initialState,
    mode,
    moduleContentRef,
    status
  )

  const value = {
    mode,
    moduleState,
    dispatch,
    moduleUI,
    exportModuleState,
    importModuleState,
    moduleId,
    programId,
    loadUserModuleRecord,
    hasModuleBeenEdited,
    accessType,
    setAccessType,
    // userModuleData,
  }

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
}
